import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "../../lib/cn";

export interface TabsProps<T extends string> {
  tabs: readonly T[];
  value: T;
  onChange: (tab: T) => void;
  label: string;
  className?: string;
}

/**
 * Tab strip with an indicator that slides between tabs instead of jumping.
 *
 * The indicator is one absolutely-positioned element moved with `transform: translateX` and
 * scaled by width, so switching tabs animates on the compositor and never re-lays-out the row.
 * The alternative — a `border-bottom` on each tab, which is what this replaced — cannot animate
 * between elements at all; the underline simply appeared somewhere else.
 *
 * Roving focus, `aria-selected` and arrow-key movement are preserved from the original hand-rolled
 * strip; only the indicator is new.
 */
export function Tabs<T extends string>({ tabs, value, onChange, label, className }: TabsProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  // useLayoutEffect so the indicator is positioned before paint — with useEffect the first
  // render shows it at 0 and it visibly slides in from the left on mount.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const active = list.querySelector<HTMLElement>('[data-active="true"]');
    if (!active) return;
    setIndicator({ left: active.offsetLeft, width: active.offsetWidth });
  }, [value, tabs]);

  // Font loading and container resize both change tab widths after mount.
  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      const active = list.querySelector<HTMLElement>('[data-active="true"]');
      if (active) setIndicator({ left: active.offsetLeft, width: active.offsetWidth });
    });
    observer.observe(list);
    return () => observer.disconnect();
  }, []);

  function onKeyDown(event: React.KeyboardEvent) {
    const index = tabs.indexOf(value);
    if (index === -1) return;
    const delta = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (delta === 0) return;
    event.preventDefault();
    const next = tabs[(index + delta + tabs.length) % tabs.length];
    if (next !== undefined) onChange(next);
  }

  return (
    <div className={cn("border-b border-border", className)}>
      <div
        ref={listRef}
        role="tablist"
        aria-label={label}
        onKeyDown={onKeyDown}
        className="no-scrollbar relative flex gap-1 overflow-x-auto"
      >
        {tabs.map((name) => {
          const active = name === value;
          return (
            <button
              key={name}
              role="tab"
              type="button"
              data-active={active}
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              onClick={() => onChange(name)}
              className={cn(
                "relative whitespace-nowrap rounded-t-control px-4 py-2.5 text-sm font-medium",
                "transition-colors duration-fast ease-standard",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
                active ? "text-text" : "text-text-muted hover:text-text",
              )}
            >
              {name}
            </button>
          );
        })}

        {indicator && (
          <span
            aria-hidden
            className="absolute bottom-0 left-0 h-0.5 rounded-pill bg-accent transition-transform duration-base ease-standard motion-reduce:transition-none"
            style={{
              width: indicator.width,
              transform: `translateX(${indicator.left}px)`,
            }}
          />
        )}
      </div>
    </div>
  );
}
